'use client'

import {
  Combobox,
  Group,
  ScrollArea,
  Text,
  UnstyledButton,
  useCombobox,
  useMantineTheme,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { ReactNode, useState } from 'react'

import IconCheckmark from '@/assets/icons/checkmark.svg'
import IconSearch from '@/assets/icons/search.svg'
import InteractiveChevronIcon from '@/components/basic/InteractiveChevronIcon'

import styles from './styles.module.css'

interface SearchableSelectProps<T> {
  /** List of options */
  options: T[]
  /** Currently selected value */
  value: T
  /** Callback when selection changes */
  onChange?: (item: T) => void
  /** Get unique key for an option */
  getOptionKey: (item: T) => string
  /** Get display label for an option (shown on button) */
  getOptionLabel: (item: T) => string
  /** Custom render function for option content (optional) */
  renderOptionContent?: (item: T, isSelected: boolean) => ReactNode
  /** Search filter function */
  searchFilter: (item: T, search: string) => boolean
  /** Search input placeholder */
  searchPlaceholder?: string
  /** Show selected option at the top of the list */
  showSelectedFirst?: boolean
}

export default function SearchableSelect<T>({
  options,
  value,
  onChange,
  getOptionKey,
  getOptionLabel,
  renderOptionContent,
  searchFilter,
  searchPlaceholder = 'Search',
  showSelectedFirst = false,
}: SearchableSelectProps<T>) {
  const [search, setSearch] = useState('')

  const combobox = useCombobox({
    onDropdownClose: () => {
      setSearch('')
    },
  })

  const theme = useMantineTheme()
  const midScaleMatches = useMediaQuery(`(min-width: ${theme.breakpoints.midScale})`, true, {
    getInitialValueInEffect: false,
  })
  const dropdownPosition = midScaleMatches ? 'bottom-end' : 'bottom-start'

  // Filter options based on search
  const filteredOptions = !search.trim()
    ? options
    : options.filter(item => searchFilter(item, search.toLowerCase().trim()))

  // Move selected option to first place if enabled
  const sortedOptions = showSelectedFirst
    ? (() => {
        const selectedIndex = filteredOptions.findIndex(
          item => getOptionKey(item) === getOptionKey(value),
        )
        if (selectedIndex === -1) return filteredOptions

        const optionsCopy = [...filteredOptions]
        const [selectedOption] = optionsCopy.splice(selectedIndex, 1)
        return [selectedOption, ...optionsCopy]
      })()
    : filteredOptions

  // Render dropdown options
  const dropdownOptions = sortedOptions.map(item => {
    const key = getOptionKey(item)
    const isSelected = getOptionKey(value) === key

    return (
      <Combobox.Option value={key} key={key} active={isSelected}>
        <Group gap="2px" wrap="nowrap">
          {isSelected && <IconCheckmark width={20} style={{ flexShrink: 0 }} />}
          {renderOptionContent ? (
            renderOptionContent(item, isSelected)
          ) : (
            <Text variant="body" lineClamp={2}>
              {getOptionLabel(item)}
            </Text>
          )}
        </Group>
      </Combobox.Option>
    )
  })

  function handleChange(key: string) {
    const selectedItem = options.find(item => getOptionKey(item) === key)
    if (selectedItem) {
      onChange?.(selectedItem)
    }
    combobox.closeDropdown()
  }

  return (
    <Combobox
      // NOTE: Dropdown position value may change based on the screen size after rendering in clients.
      // So we need to use `key` to force re-render the component when the position changes, making `preventPositionChangeWhenVisible` (lock the position) effective.
      key={dropdownPosition}
      classNames={styles}
      store={combobox}
      width={240}
      position={dropdownPosition}
      onOptionSubmit={handleChange}
    >
      <Combobox.Target>
        <UnstyledButton
          className={styles.trigger}
          data-state={combobox.dropdownOpened ? 'open' : 'closed'}
          onClick={() => combobox.toggleDropdown()}
        >
          <Group gap="8px" wrap="nowrap">
            <Text variant="body" maw="200px" truncate>
              {getOptionLabel(value)}
            </Text>
            <InteractiveChevronIcon isOpen={combobox.dropdownOpened} />
          </Group>
        </UnstyledButton>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={event => {
            combobox.updateSelectedOptionIndex()
            setSearch(event.currentTarget.value)
          }}
          placeholder={searchPlaceholder}
          leftSection={
            <IconSearch width={20} fill="var(--content-secondary)" style={{ marginLeft: '8px' }} />
          }
        />
        <Combobox.Options>
          <ScrollArea.Autosize type="auto" mah={352}>
            {dropdownOptions.length > 0 ? (
              dropdownOptions
            ) : (
              <Combobox.Empty p="md">No results found</Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
